import { Component, OnDestroy, OnInit } from '@angular/core';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { NgZone } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GridApi } from 'ag-grid-community';
import { IdbService } from 'app/services/idb.service';
import { MapBroadcastService } from '../map-broadcast.service';
import { Subject, takeUntil } from 'rxjs';
import { SiteReviewService } from 'app/services/site-review.service';
import { Command } from 'app/models/command';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { DataStoreService } from 'app/services/datastore.service';
import { MapYard, toMapYard, yardFullAddress } from './yards';

@Component({
  selector: 'app-jobs-active-map-view',
  standalone: false,
  templateUrl: './jobs-active-map-view.html',
  styleUrl: './jobs-active-map-view.scss'
})
export class JobsActiveMapView implements OnInit, OnDestroy {

  //@ViewChild(GoogleMap) map!: GoogleMap;
  private googleMap!: google.maps.Map;
  private destroy$ = new Subject<void>();

  center: google.maps.LatLngLiteral = { lat: 37.7749, lng: -122.4194 };
  zoom = 12;
  jobs: any[] = [];

  markers: google.maps.Marker[] = [];
  selectedMarker: google.maps.Marker | null = null;
  clusterer?: MarkerClusterer;
  infoWindow = new google.maps.InfoWindow();
  markerByJobId: Map<number, google.maps.Marker> = new Map();
  jobMap: Map<google.maps.Marker, any> = new Map();

  currentRoute: any[] = [];
  isPlanningRoute = false;
  routeFilterActive = false;
  isInitialLoad = true;

  gridApi!: GridApi;
  sortPopoverOpen = false;

  showYards = true;
  yards: MapYard[] = [];
  private yardMarkers: google.maps.Marker[] = [];
  private yardInfoWindow = new google.maps.InfoWindow();
  private yardsBuilt = false;

  private readonly KNOWN_BAD_LATITUDE = 37.859816634728;
  private jobsSubscribed = false;
  private geocodingInProgress = new Set<number>();
  private geocodeCache: Map<number, { lat: number, lng: number }> = new Map();

  constructor( 
    private ngZone: NgZone,
    private snackBar: MatSnackBar,
    private idbService: IdbService,
    private mapBroadcast: MapBroadcastService,
    private db: SiteReviewService,
    private dataStore: DataStoreService
  ){}

  ngOnInit(): void {
    (window as any).addToRouteFromPopup = (jobId: number) => {
  const job = this.jobs.find(j => j.jobID === jobId);
  if (!job) return;
  this.ngZone.run(() => {
    if (this.currentRoute.some(j => j.jobID === job.jobID)) {
      this.removeFromRoute(job);
    } else {
      this.addToRoute(job);
    }
    const marker = this.markerByJobId.get(jobId);
    if (marker) this.showJobInfo(marker, job);
  });
};
  }
  
//   onMapReady(map: google.maps.Map) {
//   this.googleMap = map;

//   map.setOptions({ maxZoom: 16, minZoom: 4 });

//   this.mapBroadcast.jobs$
//   .pipe(takeUntil(this.destroy$))
//   .subscribe(async payload => {
//     if (!payload) {
//       await this.loadJobsFromIndexedDB();
//       this.buildMarkersWithClustering(false);
//       return;
//     }

//     const { jobs } = payload;

//     this.ngZone.run(() => {
//       this.jobs = this.filterValidJobs(jobs);

//       if (this.isInitialLoad) {
//         // First broadcast: stay on SF, just plot markers
//         this.isInitialLoad = false;
//         this.buildMarkersWithClustering(false);
//         this.notifyJobsOutsideInitialView();
//       } else {
//         // Subsequent broadcasts: always fit to markers
//         this.buildMarkersWithClustering(true);
//       }
//     });
//   });

//   this.mapBroadcast.notifyMapReady();
// }

onMapReady(map: google.maps.Map) {
  this.googleMap = map;

  map.setOptions({ maxZoom: 16, minZoom: 4 });

  if (!this.jobsSubscribed) {
    this.jobsSubscribed = true;

    this.mapBroadcast.jobs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(payload => {
        // Ignore the BehaviorSubject's initial null — the grid tab will push
        // the real (already-filtered) payload via BroadcastChannel right after.
        if (!payload) {
          return;
        }

        const { jobs } = payload;

        this.ngZone.run(() => {
          const { valid, needsGeocode } = this.filterValidJobs(jobs);
          this.jobs = valid;

          if (this.isInitialLoad) {
            this.isInitialLoad = false;
            this.buildMarkersWithClustering(false);
            this.notifyJobsOutsideInitialView();
          } else {
            this.buildMarkersWithClustering(true);
          }

          if (needsGeocode.length > 0) {
            this.geocodeMissingJobs(needsGeocode);
          }
        });
      });
  }

  this.buildYardMarkers();

  this.mapBroadcast.notifyMapReady();
}


ngOnDestroy() {
  this.destroy$.next();
  this.destroy$.complete();
  this.geocodingInProgress.clear();
  this.geocodeCache.clear();
  this.clearYardMarkers();
}

// private filterValidJobs(jobs: any[]): any[] {
//   return jobs.filter(job => {
//     const lat = parseFloat(job.jobLatitude);
//     const lng = parseFloat(job.jobLongitude);

//     if (isNaN(lat) || isNaN(lng)) return false;
//     if (lat === 37.859816634728) return false; // known bad default

//     // Bounding box covering continental USA + Alaska + Hawaii
//     const inUSA = lat >= 18.0 && lat <= 71.5 && lng >= -179.5 && lng <= -66.0;

//     return inUSA;
//   });
// }

private filterValidJobs(jobs: any[]): { valid: any[], needsGeocode: any[] } {
  const valid: any[] = [];
  const needsGeocode: any[] = [];

  jobs.forEach(job => {
    const lat = parseFloat(job.jobLatitude);
    const lng = parseFloat(job.jobLongitude);

    // console.log('[DEBUG] job', job.jobID, {
    //   jobLatitude: job.jobLatitude,
    //   jobLongitude: job.jobLongitude,
    //   parsedLat: lat,
    //   parsedLng: lng,
    //   isWithinUSA: this.isWithinUSA(lat, lng),
    //   jobAddress: job.jobAddress,
    //   jobCity: job.jobCity,
    //   hasValidAddress: this.hasValidAddress(job)
    // });

    if (this.isWithinUSA(lat, lng)) {
      valid.push(job);
      return;
    }

    const cached = this.geocodeCache.get(job.jobID);
    if (cached) {
      job.jobLatitude = cached.lat.toString();
      job.jobLongitude = cached.lng.toString();
      valid.push(job);
      return;
    }

    if (this.geocodingInProgress.has(job.jobID)) {
      return;
    }

    if (this.hasValidAddress(job)) {
      needsGeocode.push(job);
    }
  });

  return { valid, needsGeocode };
}

  // private async loadJobsFromIndexedDB() {
  //   try {
  //     await this.idbService.initializeDB();
  //     const allJobs = await this.idbService.list('jobs');

  //     if (allJobs && Array.isArray(allJobs)) {
  //       this.jobs = this.filterValidJobs(allJobs);

  //     }
  //   } catch (error) {
  //     console.error('Error loading jobs from IndexedDB:', error);
  //   }
  // }

  private async loadJobsFromIndexedDB() {
  try {
    await this.idbService.initializeDB();
    const allJobs = await this.idbService.list('jobs');

    if (allJobs && Array.isArray(allJobs)) {
      const { valid, needsGeocode } = this.filterValidJobs(allJobs);
      this.jobs = valid;

      if (needsGeocode.length > 0) {
        this.geocodeMissingJobs(needsGeocode);
      }
    }
  } catch (error) {
    console.error('Error loading jobs from IndexedDB:', error);
  }
}

 private buildMarkersWithClustering(fitToJobs = false) {
  console.log('[DEBUG] buildMarkersWithClustering called with', this.jobs.length, 'jobs. fitToJobs:', fitToJobs);
  if (!this.googleMap) return;

  this.markers.forEach(m => m.setMap(null));
  this.markers = [];
  this.markerByJobId.clear();
  this.jobMap.clear();

  if (this.clusterer) {
    this.clusterer.clearMarkers();
  }

  this.markers = this.jobs.map(job => {
    const marker = new google.maps.Marker({
      position: {
        lat: parseFloat(job.jobLatitude),
        lng: parseFloat(job.jobLongitude)
      },
      title: `${job.pMNumber || 'No PM'} - ${job.jobName || job.jobAddress || ''}`
    });

    // marker.addListener('click', () => {
    //   this.ngZone.run(() => {
    //     if (this.isPlanningRoute) {
    //       this.addToRoute(job);
    //     } else {
    //       this.showJobInfo(marker, job);
    //     }
    //   });
    // });
    marker.addListener('click', () => {
      this.ngZone.run(() => {
        this.selectMarker(marker);
        this.showJobInfo(marker, job);
      });
    });

    this.jobMap.set(marker, job);
    this.markerByJobId.set(job.jobID, marker);
    return marker;
  });

  this.clusterer = new MarkerClusterer({
    map: this.googleMap,
    markers: this.markers,
    algorithmOptions: { maxZoom: 15 }
  });

  if (fitToJobs) {
    setTimeout(() => this.fitMapToMarkers(), 100);
  }

  if (this.jobs.length === 0) {
    this.snackBar.open('No jobs match the current filters.', 'OK', { duration: 4000 });
  }
}

private selectMarker(marker: google.maps.Marker) {
  if (this.selectedMarker && this.selectedMarker !== marker) {
    this.restoreMarkerAppearance(this.selectedMarker);
  }
  this.selectedMarker = marker;
  const job = this.jobMap.get(marker);
  const index = job ? this.currentRoute.findIndex(j => j.jobID === job.jobID) : -1;
  if (index === -1) {
    marker.setIcon({
      url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png',
      scaledSize: new google.maps.Size(42, 42)
    });
  }
}

private restoreMarkerAppearance(marker: google.maps.Marker) {
  const job = this.jobMap.get(marker);
  if (!job) return;
  const index = this.currentRoute.findIndex(j => j.jobID === job.jobID);
  if (index !== -1) {
    marker.setIcon({
      url: 'http://maps.google.com/mapfiles/ms/icons/orange-dot.png',
      scaledSize: new google.maps.Size(42, 42)
    });
    marker.setLabel({
      text: (index + 1).toString(),
      color: '#000000',
      fontSize: '15px',
      fontWeight: 'bold'
    });
  } else {
    marker.setIcon(null);
    marker.setLabel(null);
  }
}

showJobInfo(marker: google.maps.Marker, job: any) {
  //console.log('showJobInfo', job);
  const address = [job.jobAddress, job.jobCity, job.jobState].filter(Boolean).join(',');
  const geocodeBtnId = `geocode-btn-${job.jobID}`;
  const isInRoute = this.currentRoute.some(j => j.jobID === job.jobID);
  const electricEstimatedStartDate = job.electricEstimatedStartDate ? new Date(job.electricEstimatedStartDate).toLocaleDateString() : 'N/A';
  const electricActualEndDate = job.electricActualEndDate ? new Date(job.electricActualEndDate).toLocaleDateString() : 'N/A';
  const civilEstimatedStartDate = job.civilEstimatedStartDate ? new Date(job.civilEstimatedStartDate).toLocaleDateString() : 'N/A';
  const civilActualEndDate = job.civilActualEndDate ? new Date(job.civilActualEndDate).toLocaleDateString() : 'N/A';

  const routeBtn = isInRoute
    ? `<button onclick="window.addToRouteFromPopup(${job.jobID})" 
              class="info-button danger">
         <span class="material-icons">remove_circle</span> Remove from Route
       </button>`
    : `<button onclick="window.addToRouteFromPopup(${job.jobID})" 
              class="info-button secondary">
         <span class="material-icons">add_circle</span> Add to Route
       </button>`;

  const content = `
    <div class="custom-info-window">
      <div class="info-header">
        <h3>${job.pMNumber || 'No PM'} - ${address || job.jobName || 'No name'}</h3>
        <p class="info-coords">${job.jobLatitude}, ${job.jobLongitude}</p>
        
        <div class="additional-info">
          <p class="additional-info-item">
            <span> <b>Electric:</b> ${job.generalForeman || 'N/A'} </span>  <span> <b>Est. Start:</b> ${electricEstimatedStartDate} </span> <span> <b>End:</b> ${electricActualEndDate}</span>
          </p>
          <p class="additional-info-item">
            <span> <b>Civil:</b> ${job.civilGeneralForeman || 'N/A'} </span> <span> <b>Est. Start:</b> ${civilEstimatedStartDate} </span> <span> <b>End:</b> ${civilActualEndDate}</span>
          </p>
        </div>
      </div>
      <div class="info-actions">
        <a href="${window.location.origin}/reviews/jobs/${job.jobID}" target="_blank" class="info-button primary">
          <span class="material-icons">work</span> View Job
        </a>
        ${job.jobGDriveFolder ? `
          <a href="${job.jobGDriveFolder}" target="_blank" class="info-button secondary">
            <span class="material-icons">folder</span> Job Folder
          </a>` : ''}

        <a href="https://www.google.com/maps/dir/?api=1&destination=${job.jobLatitude},${job.jobLongitude}" 
           target="_blank" class="info-button secondary">
          <span class="material-icons">directions</span> Directions
        </a>

        ${routeBtn}

        ${address ? `
          <button id="${geocodeBtnId}" class="info-button geocode-btn">
            <span class="material-icons">my_location</span> Re-geocode
          </button>` : ''}
      </div>
    </div>
  `;

  this.infoWindow.setContent(content);
  this.infoWindow.open(this.googleMap, marker);

  google.maps.event.addListenerOnce(this.infoWindow, 'domready', () => {
    const btn = document.getElementById(geocodeBtnId);
    if (btn) btn.addEventListener('click', () => this.reGeocodeJob(marker, job, btn));
  });
}

  // showJobInfo(marker: google.maps.Marker, job: any) {
  //   const address = [job.jobAddress, job.jobCity, job.jobState, job.postCode].filter(Boolean).join(',');
  //   const geocodeBtnId = `geocode-btn-${job.jobID}`;
  //   const isInRoute = this.currentRoute.some(j => j.jobID === job.jobID);
  
  //   const content = `
  //     <div class="custom-info-window">
  //       <div class="info-header">
  //         <h3>${job.pMNumber || 'No PM'} - ${job.jobName || 'No name'}</h3>
  //         <p class="info-coords">${job.jobLatitude}, ${job.jobLongitude}</p>
  //         ${address ? `<p class="info-address">${[job.jobAddress, job.jobCity, job.jobState].filter(Boolean).join(', ')}</p>` : ''}
  //       </div>
  //       <div class="info-actions">
  //         <a href="${window.location.origin}/reviews/jobs/${job.jobID}" target="_blank" class="info-button primary">
  //           <span class="material-icons">work</span> View Job
  //         </a>
  //         ${job.jobGDriveFolder ? `
  //           <a href="${job.jobGDriveFolder}" target="_blank" class="info-button secondary">
  //             <span class="material-icons">folder</span> Job Folder
  //           </a>` : ''}
  
  //         <a href="https://www.google.com/maps/dir/?api=1&destination=${job.jobLatitude},${job.jobLongitude}" 
  //            target="_blank" class="info-button secondary">
  //           <span class="material-icons">directions</span> Directions
  //         </a>

  //          <button onclick="window.addToRouteFromPopup(${job.jobID})" 
  //                 class="info-button ${isInRoute ? 'success' : 'secondary'}">
  //           <span class="material-icons">${isInRoute ? 'check_circle' : 'add_circle'}</span>
  //           ${isInRoute ? 'Added to Route' : 'Remove from Route'}
  //         </button>
  
  //         ${address ? `
  //           <button id="${geocodeBtnId}" class="info-button geocode-btn">
  //             <span class="material-icons">my_location</span> Re-geocode
  //           </button>` : ''}
  //       </div>
  //     </div>
  //   `;
  
  //   this.infoWindow.setContent(content);
  //   this.infoWindow.open(this.googleMap, marker);
  
  //   google.maps.event.addListenerOnce(this.infoWindow, 'domready', () => {
  //     const btn = document.getElementById(geocodeBtnId);
  //     if (btn) btn.addEventListener('click', () => this.reGeocodeJob(marker, job, btn));
  //   });
  // }

  private reGeocodeJob(marker: google.maps.Marker, job: any, btn: HTMLElement) {
      btn.classList.add('loading');
      btn.innerHTML = '<span class="material-icons spin">sync</span> Checking...';
  
      this.db.validateJobAddress(job.jobID).subscribe({
        next: (res: any) => {
          const newLat = res.latitude;
          const newLng = res.longitude;
  
          if (!newLat || !newLng) {
            btn.classList.remove('loading');
            btn.innerHTML = '<span class="material-icons">error_outline</span> No result';
            return;
          }
  
          const oldLat = parseFloat(job.jobLatitude);
          const oldLng = parseFloat(job.jobLongitude);
          const moved = Math.abs(oldLat - newLat) > 0.0001 || Math.abs(oldLng - newLng) > 0.0001;
  
          if (!moved) {
            btn.classList.remove('loading');
            btn.innerHTML = '<span class="material-icons">check_circle</span> Location OK';
            btn.classList.add('success');
            return;
          }
  
          const cmdLat = new Command();
          cmdLat.procedure = 'cmdFieldUpdate';
          cmdLat.addParameter('Table', 'Job');
          cmdLat.addParameter('Field', 'Latitude');
          cmdLat.addParameter('Value', newLat);
          cmdLat.addParameter('Key', 'JobID');
          cmdLat.addParameter('ID', job.jobID);
  
          const cmdLng = new Command();
          cmdLng.procedure = 'cmdFieldUpdate';
          cmdLng.addParameter('Table', 'Job');
          cmdLng.addParameter('Field', 'Longitude');
          cmdLng.addParameter('Value', newLng);
          cmdLng.addParameter('Key', 'JobID');
          cmdLng.addParameter('ID', job.jobID);
  
          this.db.command(cmdLat).subscribe();
          this.db.command(cmdLng).subscribe();
  
          job.jobLatitude = newLat.toString();
          job.jobLongitude = newLng.toString();
  
          this.idbService.update('jobs', job.jobID, job).then(() => {
            //console.log(`IndexedDB updated for job ${job.jobID}`);
          }).catch(err => {
            console.error(`Failed to update IndexedDB for job ${job.jobID}:`, err);
          });
  
          marker.setPosition({ lat: newLat, lng: newLng });
  
          btn.classList.remove('loading');
          btn.innerHTML = '<span class="material-icons">check_circle</span> Updated';
          btn.classList.add('success');
          //console.log(`Re-geocoded job ${job.jobID}: (${oldLat},${oldLng}) -> (${newLat},${newLng})`);
        },
        error: (err) => {
          console.error('Re-geocode failed:', err);
          btn.classList.remove('loading');
          btn.innerHTML = '<span class="material-icons">error_outline</span> Failed';
        }
      });
    }

  toggleRoutePlanning() {
    this.isPlanningRoute = !this.isPlanningRoute;
    
    if (!this.isPlanningRoute) {
      this.infoWindow.close();
      this.routeFilterActive = false;
      if (this.gridApi) this.gridApi.onFilterChanged();
    }
    
    this.updateAllMarkersAppearance();
  }


  addToRoute(job: any) {
  if (this.currentRoute.some(j => j.jobID === job.jobID)) return;
  
  this.currentRoute.push(job);
  this.updateAllMarkersAppearance();
}

removeFromRoute(job: any) {
  this.currentRoute = this.currentRoute.filter(j => j.jobID !== job.jobID);
  this.updateAllMarkersAppearance();
  if (this.selectedMarker) {
    const selectedJob = this.jobMap.get(this.selectedMarker);
    if (selectedJob?.jobID === job.jobID) {
      this.selectedMarker.setIcon({
        url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png',
        scaledSize: new google.maps.Size(42, 42)
      });
      this.selectedMarker.setLabel(null);
    }
  }
}

sortRoute(event: MouseEvent) {
  this.sortPopoverOpen = !this.sortPopoverOpen;
}

onRouteDrop(event: CdkDragDrop<any[]>) {
  moveItemInArray(this.currentRoute, event.previousIndex, event.currentIndex);
  this.updateAllMarkersAppearance();
}

clearRoute() {
  this.currentRoute = [];
  this.updateAllMarkersAppearance();
  this.routeFilterActive = false;
  
  if (this.gridApi) {
    this.gridApi.onFilterChanged();
  }
}

getGoogleMapsRouteUrl(): string {
  if (this.currentRoute.length === 0) return '';

  const points = this.currentRoute.map(job => `${job.jobLatitude},${job.jobLongitude}`);

  if (points.length === 1) {
    return `https://www.google.com/maps/dir/?api=1&destination=${points[0]}&travelmode=driving`;
  }

  const destination = points.pop()!;
  const waypoints = points.length > 0 ? points.join('|') : '';

  return `https://www.google.com/maps/dir/?api=1&waypoints=${waypoints}&destination=${destination}&travelmode=driving`;
}

openRouteInGoogleMaps() {
  const url = this.getGoogleMapsRouteUrl();
  if (url) window.open(url, '_blank');
}

async shareRoute() {
  const url = this.getGoogleMapsRouteUrl();
  if (!url) return;

  try {
    await navigator.clipboard.writeText(url);

    this.snackBar.open('✅ Route link copied to clipboard!', 'OK', {
      duration: 4000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['success-snackbar']
    });

  } catch (err) {
    this.snackBar.open('❌ Could not copy link', 'Close', {
      duration: 3000,
      panelClass: ['error-snackbar']
    });
  }
}

// private updateAllMarkersAppearance() {
//   this.markers.forEach(marker => {
//     const job = this.jobMap.get(marker);
//     if (!job) return;

//     const index = this.currentRoute.findIndex(j => j.jobID === job.jobID);
    
//     if (index !== -1) {
//       marker.setIcon({
//         url: 'http://maps.google.com/mapfiles/ms/icons/orange-dot.png',
//         scaledSize: new google.maps.Size(42, 42)
//       });
//       marker.setLabel({
//         text: (index + 1).toString(),
//         color: '#000000',
//         fontSize: '15px',
//         fontWeight: 'bold'
//       });
//     } else {
//       marker.setIcon(null);
//       marker.setLabel(null);
//     }
//   });
// }

private updateAllMarkersAppearance() {
  this.markers.forEach(marker => {
    const job = this.jobMap.get(marker);
    if (!job) return;

    const index = this.currentRoute.findIndex(j => j.jobID === job.jobID);

    if (index !== -1) {
      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="48" viewBox="0 0 36 48">
          <!-- Pin -->
          <path d="M18 0C8.06 0 0 8.06 0 18c0 12.42 18 30 18 30S36 30.42 36 18C36 8.06 27.94 0 18 0z" fill="#E65100"/>
          <path d="M18 0C8.06 0 0 8.06 0 18c0 12.42 18 30 18 30S36 30.42 36 18C36 8.06 27.94 0 18 0z" fill="#FF6D00" opacity="0.85"/>
          <!-- Inner circle background -->
          <circle cx="18" cy="18" r="11" fill="white"/>
          <!-- Number -->
          <text x="18" y="23" 
                font-family="Arial, sans-serif" 
                font-size="13" 
                font-weight="bold" 
                fill="#E65100" 
                text-anchor="middle">${index + 1}</text>
        </svg>
      `;

      const encoded = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);

      marker.setIcon({
        url: encoded,
        scaledSize: new google.maps.Size(36, 48),
        anchor: new google.maps.Point(18, 48)
      });
      marker.setLabel(null);

    } else {
      marker.setIcon(null);
      marker.setLabel(null);
    }
  });
}

private fitMapToMarkers() {
  // console.log('=== fitMapToMarkers called ===');
  // console.log('googleMap exists:', !!this.googleMap);
  // console.log('jobs count:', this.jobs.length);
  
  if (!this.googleMap || this.jobs.length === 0) return;

  if (this.jobs.length === 1) {
    const lat = parseFloat(this.jobs[0].jobLatitude);
    const lng = parseFloat(this.jobs[0].jobLongitude);
    console.log('panning to single marker:', lat, lng);
    this.googleMap.panTo({ lat, lng });
    this.googleMap.setZoom(13);
    return;
  }

  const bounds = new google.maps.LatLngBounds();
  this.jobs.forEach(job => {
    bounds.extend({
      lat: parseFloat(job.jobLatitude),
      lng: parseFloat(job.jobLongitude)
    });
  });

  // console.log('bounds:', bounds.toString());
  // console.log('calling fitBounds...');
  this.googleMap.fitBounds(bounds, 80);
  // console.log('fitBounds called, current center:', this.googleMap.getCenter()?.toString());
}

private notifyJobsOutsideInitialView() {

  const sfBounds = {
    north: 38.3,
    south: 37.0,
    east: -121.7,
    west: -123.0
  };

  const outsideJobs = this.jobs.filter(job => {

    const lat = parseFloat(job.jobLatitude);
    const lng = parseFloat(job.jobLongitude);

    return (
      lat < sfBounds.south ||
      lat > sfBounds.north ||
      lng < sfBounds.west ||
      lng > sfBounds.east
    );
  });

  if (outsideJobs.length === 0) {
    return;
  }

  const sampleJobs = outsideJobs
    .slice(0, 3)
    .map(j => j.pMNumber || j.jobName || j.jobID)
    .join(', ');

  this.snackBar.open(
    `${outsideJobs.length} jobs are outside the initial San Francisco view (${sampleJobs}${outsideJobs.length > 3 ? '...' : ''}).`,
    'OK',
    {
      duration: 7000
    }
  );
}

private geocodeMissingJobs(jobs: any[]) {
  const pending = jobs.filter(job => !this.geocodingInProgress.has(job.jobID) && !this.geocodeCache.has(job.jobID));
  if (!pending.length) return;

  pending.forEach(job => this.geocodingInProgress.add(job.jobID));

  const geocoder = new google.maps.Geocoder();
  let index = 0;

  const processNext = () => {
    if (index >= pending.length) return;

    const job = pending[index];
    index++;

    const address = [job.jobAddress, job.jobCity, job.jobState].filter(Boolean).join(', ');

    geocoder.geocode(
      {
        address,
        componentRestrictions: { country: 'US' }
      },
      (results, status) => {
        this.geocodingInProgress.delete(job.jobID);

        if (status === google.maps.GeocoderStatus.OK && results[0]) {
          const location = results[0].geometry.location;
          const lat = location.lat();
          const lng = location.lng();

          if (this.isWithinUSA(lat, lng)) {
            job.jobLatitude = lat.toString();
            job.jobLongitude = lng.toString();

            // Cache in memory only: the backend isn't being updated with this result,
            // so we need to keep re-applying it every time this job comes through a new broadcast
            this.geocodeCache.set(job.jobID, { lat, lng });

            this.ngZone.run(() => {
              this.jobs = this.jobs.filter(j => j.jobID !== job.jobID);
              this.jobs.push(job);
              this.addSingleMarker(job);
            });
          } else {
            console.warn(`Geocoded result for job ${job.jobID} (${address}) fell outside USA bounds: ${lat}, ${lng}`);
          }
        } else {
          console.warn(`Geocoding failed for job ${job.jobID} (${address}): ${status}`);
        }

        setTimeout(processNext, 200);
      }
    );
  };

  processNext();
}

private addSingleMarker(job: any) {
  if (!this.googleMap) return;

  // Remove any pre-existing marker for this job before adding the new one
  const existingMarker = this.markerByJobId.get(job.jobID);
  if (existingMarker) {
    existingMarker.setMap(null);
    if (this.clusterer) this.clusterer.removeMarker(existingMarker);
    this.markers = this.markers.filter(m => m !== existingMarker);
    this.jobMap.delete(existingMarker);
    this.markerByJobId.delete(job.jobID);
  }

  const marker = new google.maps.Marker({
    position: {
      lat: parseFloat(job.jobLatitude),
      lng: parseFloat(job.jobLongitude)
    },
    title: `${job.pMNumber || 'No PM'} - ${job.jobName || job.jobAddress || ''}`
  });

  marker.addListener('click', () => {
    this.ngZone.run(() => {
      this.selectMarker(marker);
      this.showJobInfo(marker, job);
    });
  });

  this.jobMap.set(marker, job);
  this.markerByJobId.set(job.jobID, marker);
  this.markers.push(marker);

  if (this.clusterer) {
    this.clusterer.addMarker(marker);
  } else {
    marker.setMap(this.googleMap);
  }
}

private isWithinUSA(lat: number, lng: number): boolean {
  if (isNaN(lat) || isNaN(lng)) return false;
  if (lat === this.KNOWN_BAD_LATITUDE) return false;

  return lat >= 18.0 && lat <= 71.5 && lng >= -179.5 && lng <= -66.0;
}

private hasValidAddress(job: any): boolean {
  const address = job.jobAddress?.toString().trim();
  const city = job.jobCity?.toString().trim();

  if (!address || !city) return false;
  if (address.length < 5) return false;

  const looksLikeAddress = /\d/.test(address) && /[a-zA-Z].*[a-zA-Z]/.test(address);

  return looksLikeAddress;
}



toggleYards() {
  this.showYards = !this.showYards;

  if (!this.showYards) {
    this.yardInfoWindow.close();
  }

  this.yardMarkers.forEach(m => m.setMap(this.showYards ? this.googleMap : null));

  // First enable after a failed/never-run build: try again.
  if (this.showYards && this.yardMarkers.length === 0) {
    this.yardsBuilt = false;
    this.buildYardMarkers();
  }
}

private async buildYardMarkers() {
  if (!this.googleMap || this.yardsBuilt) return;
  this.yardsBuilt = true;

  let rows: any[];
  try {
    rows = await this.dataStore.getYards();
  } catch (err) {
    console.error('Could not load yards for map:', err);
    this.yardsBuilt = false;
    return;
  }

  // Yards without usable DB coordinates are skipped — no geocoding fallback.
  // Fix missing pins via "Get Lat/Lon from Address" on the Yards grid.
  this.yards = (rows || [])
    .filter(row => row.status !== 'Inactive')
    .map(toMapYard)
    .filter(y => y.lat != null && y.lng != null && this.isWithinUSA(y.lat, y.lng));

  this.yards.forEach(yard => this.addYardMarker(yard));
}

private addYardMarker(yard: MapYard) {
  if (!this.googleMap || yard.lat == null || yard.lng == null) return;

  const marker = new google.maps.Marker({
    position: { lat: yard.lat, lng: yard.lng },
    title: yard.name,
    icon: this.buildYardIcon(),
    // Yards sit under the job pins so a yard never hides a job marker.
    zIndex: 1,
    map: this.showYards ? this.googleMap : null
  });

  marker.addListener('click', () => {
    this.ngZone.run(() => this.showYardInfo(marker, yard));
  });

  this.yardMarkers.push(marker);
}

private buildYardIcon(): google.maps.Icon {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="34" height="44" viewBox="0 0 36 48">
      <path d="M18 0C8.06 0 0 8.06 0 18c0 12.42 18 30 18 30S36 30.42 36 18C36 8.06 27.94 0 18 0z" fill="#1B5E20"/>
      <path d="M18 0C8.06 0 0 8.06 0 18c0 12.42 18 30 18 30S36 30.42 36 18C36 8.06 27.94 0 18 0z" fill="#2E7D32" opacity="0.9"/>
      <circle cx="18" cy="18" r="11" fill="white"/>
      <path d="M11 20.5v-4.2l7-3.8 7 3.8v4.2h-2.2v-3.1h-9.6v3.1z" fill="#1B5E20"/>
      <rect x="13" y="18.5" width="10" height="5.5" fill="#1B5E20"/>
    </svg>
  `;

  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    scaledSize: new google.maps.Size(34, 44),
    anchor: new google.maps.Point(17, 44)
  };
}

private showYardInfo(marker: google.maps.Marker, yard: MapYard) {
  const address = yardFullAddress(yard);

  const content = `
    <div class="custom-info-window yard-info-window">
      <div class="info-header">
        <h3><span class="material-icons yard-badge">warehouse</span> ${yard.name}</h3>
        <p class="info-address">${address}</p>
        <p class="info-coords">${yard.lat}, ${yard.lng}</p>
      </div>
      <div class="info-actions">
        <a href="https://www.google.com/maps/dir/?api=1&destination=${yard.lat},${yard.lng}"
           target="_blank" class="info-button secondary">
          <span class="material-icons">directions</span> Directions
        </a>
      </div>
    </div>
  `;

  // Yards use their own InfoWindow so opening one doesn't close an open job popup.
  this.infoWindow.close();
  this.yardInfoWindow.setContent(content);
  this.yardInfoWindow.open(this.googleMap, marker);
}

private clearYardMarkers() {
  this.yardMarkers.forEach(m => m.setMap(null));
  this.yardMarkers = [];
  this.yardsBuilt = false;
}

}
